import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { signin, signout, generateKey } from './api';
import LocalStorage from 'utilities/localStorage';
import { resetSessionTerminationGuard } from 'utilities/apiClient';
import { markFreshLogin } from 'features/maintenance/maintenanceStorage';
import { history } from 'app/history';

const initialState = {
  loading: false,
  token: '',
  user: null,
  session_conflict: false,
  conflict_credentials: null,
  password_expired: false,
  fpc_token: '',
  expired_employee_id: ''
};

const actions = {
  LOGIN: 'login/LOGIN',
  LOGOUT: 'login/LOGOUT',
  GENERATE_LICENSE: 'login/GENERATE_LICENSE'
};

export const login = createAsyncThunk(actions.LOGIN, async (payload) => {
  const response = await signin(payload);

  if (response?.password_expired) {
    // Don't clear localStorage, don't redirect — the login screen shows a guided
    // modal with a Change Password button (handled via Redux state).
  } else if (response?.force_password_change) {
    history.push(
      `/change-password?user_id=${encodeURIComponent(
        payload.email
      )}&fpc_token=${response.fpc_token}`
    );
  } else if (response?.session_conflict) {
    // Don't clear localStorage, don't redirect — modal handles it via Redux state
  } else if (response?.maintenance) {
    // Sign-in blocked by maintenance — stay on the login page, which shows the notice.
  } else if (response?.token) {
    const { token, user } = response;
    let newUser = {
      token,
      user,
      session_token: response.session_token
    };

    // New session established — re-arm the "logged out elsewhere" notice for next time.
    resetSessionTerminationGuard();
    LocalStorage.setItem('Gravity_user', JSON.stringify(newUser));
    markFreshLogin();
    if (newUser.user.role === 'user' || newUser.user.role === 'document_cell') {
      history.push('/dashboard');
    } else if (newUser.user.role === 'qc_initiator') {
      history.push('/dashboard');
    } else if (
      newUser.user.role === 'prod_user' ||
      newUser.user.role === 'ppic_user'
    ) {
      history.push('/dashboard');
    } else if (newUser.user.role === 'qa_initiator') {
      history.push('/dashboard');
    } else if (newUser.user.role === 'update_admin') {
      history.push('/backend_mrn');
    } else if (newUser.user.role === 'admin') {
      history.push('/dashboard');
    } else if (newUser.user.role === 'finance_user') {
      history.push('/dashboard');
    } else if (newUser.user.role === 'bd_user') {
      history.push('/dashboard');
    } else if (newUser.user.role === 'qa_super_user') {
      history.push('/super_admin_dashboard');
    } else {
      history.push('/dashboard');
    }
  } else {
    localStorage.clear();
    logout();
    message.success('Logged out successfully');
    history.push('/login');
  }

  return response;
});

export const generateLicense = createAsyncThunk(
  actions.GENERATE_LICENSE,
  async (payload) => {
    const response = await generateKey('activate', payload);

    return response;
  }
);

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    resetLoginDetails: (state) => {
      state.token = '';
      state.user = null;
    },
    resetSessionConflict: (state) => {
      state.session_conflict = false;
      state.conflict_credentials = null;
    },
    resetPasswordExpired: (state) => {
      state.password_expired = false;
      state.fpc_token = '';
      state.expired_employee_id = '';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        const {
          token,
          message: msg,
          user,
          force_password_change,
          session_conflict,
          password_expired,
          fpc_token
        } = action.payload;
        state.loading = false;

        if (password_expired) {
          state.password_expired = true;
          state.fpc_token = fpc_token;
          state.expired_employee_id = action.meta.arg?.email || '';
          message.warning(msg);
        } else if (session_conflict) {
          state.session_conflict = true;
          state.conflict_credentials = action.meta.arg;
        } else if (action.payload?.maintenance) {
          message.warning(msg);
        } else {
          state.token = token;
          state.user = user;
          state.session_conflict = false;
          state.conflict_credentials = null;

          if (force_password_change) {
            message.warning(msg, [3]);
          } else if (token) {
            message.success(msg, [1]);
          } else {
            message.error(msg);
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });

    builder
      .addCase(generateLicense.pending, (state) => {
        state.loading = true;
      })
      .addCase(generateLicense.fulfilled, (state, action) => {
        const { success, message: msg } = action.payload;
        state.loading = false;

        if (success) {
          const gravityUserString = localStorage.getItem('Gravity_user');
          console.log(gravityUserString);
          const gravityUser = JSON.parse(gravityUserString);
          console.log(gravityUser, 'gravityUser');
          gravityUser.user.license = { is_active: true };
          localStorage.setItem('Gravity_user', JSON.stringify(gravityUser));
          message.success(msg, [1]);
        } else {
          message.error(msg);
        }
      })
      .addCase(generateLicense.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.loading = false;

        message.error(msg);
      });
  }
});

export const { resetLoginDetails, resetSessionConflict, resetPasswordExpired } =
  loginSlice.actions;

export const logout = () => (dispatch) => {
  const payload = {};
  signout('logout', payload);
  localStorage.clear();
  dispatch(resetLoginDetails());
  message.success('Logged out successfully');
  history.push('/login');
};

export default loginSlice.reducer;
